import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { theme } from 'lib/style';
import { useAuth } from 'lib/context';
import { Button } from 'react-covideo-common';
import { preparePrice } from 'lib/utils/invoice';
import { isNull, isEmpty } from 'lodash';
import {
  packageAdditionalInfo,
  PackageName,
} from 'lib/const/PackageAdditionalInfo';
import { MdEdit } from 'react-icons/md';
import { IoMdLock } from 'react-icons/io';
import { ModalContactAM } from 'lib/components/modal/ModalContactAM';
import { STANDARD_DATE_FORMAT } from 'lib/const/DateFormat';
import { GetSubscriptionResponse } from 'lib/api/billing/useSubscriptionQuery';
import { getSecondaryColor } from 'lib/context/theme/utils';

const SubscriptionWrapper = styled.div`
  display: flex;
  border: 1px solid #f7f8fa;
  box-shadow: 0px 4px 12px ${({ theme }) => theme.colors.black[5]};
  padding: 20px 15px 20px 20px;
  .div--left {
    width: 50%;
    display: flex;
    column-gap: 40px;
  }
  .div--right {
    width: 50%;
    display: flex;
    column-gap: 20px;
    justify-content: end;
  }
  ${theme.mediaQueryMaxWidth.lg} {
    flex-direction: column;
    row-gap: 40px;
    .div--left,
    .div--right {
      width: 100%;
      display: flex;
      flex-direction: column;
      row-gap: 20px;
    }
  }
`;

const SubscriptionDiv = styled.div`
  font-size: 14px;
  .plan--name {
    font-size: 16px;
    font-weight: 500;
    padding-top: 10px;
    color: ${({ theme }) => theme.colors.primary[100]};
    &--upcoming {
      padding-top: 10px;
      font-size: 16px;
      color: ${({ theme }) => getSecondaryColor(theme, 60)};

      font-weight: 800;
    }
  }
  .manage--btn {
    display: flex;
    align-items: center;
    justify-content: end;
    height: 100%;
  }
  .plan--trial {
    color: ${({ theme }) => theme.colors.danger[100]};
  }
  .font--14 {
    font-size: 14px;
  }
  .plan--details {
    display: flex;
    padding-top: 10px;
  }
  ${theme.mediaQueryMaxWidth.lg} {
    width: 100%;
    .manage--btn {
      justify-content: start;
    }
  }
`;

const SubscriptionDivUntill = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  min-width: max-content;
  font-size: 14px;
  ${theme.mediaQueryMaxWidth.lg} {
    width: 100%;
    justify-content: start;
  }
`;

const Wrapper = styled.div`
  background: ${({ theme }) => theme.colors.white[100]};
  box-shadow:
    0px 0px 2px ${({ theme }) => theme.colors.black[10]};
    0px 4px 8px ${({ theme }) => theme.colors.black[5]};
  border-radius: 4px;
  padding: 24px;
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Plan = styled.div`
  display: block;
`;
const Title = styled.div`
  color: ${({ theme }) => getSecondaryColor(theme)};
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
`;
type ActivePlanProps = {
  isBeingCanceled: boolean;
};
const ActivePlan = styled.div<ActivePlanProps>`
  color: ${({ theme }) => theme.colors.black[100]};
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 12px;
  ${({ isBeingCanceled, theme }) =>
    isBeingCanceled &&
    css`
      color: ${theme.colors.danger[100]};
    `}
`;
const NextPayment = styled.div`
  margin: 0 40px 0 auto;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.black[100]};
`;
const ManagePlan = styled.div``;
const BillingCycle = styled.div`
  color: ${({ theme }) => theme.colors.neutral[60]};
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
`;
const Price = styled.span`
  color: ${theme.palette.covideoOrange100};
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
`;

type Props = {
  handleMangeModal: (data: any) => void;
  setShowAmModal: (show: boolean) => void;
  addonData: any;
  subscription: GetSubscriptionResponse | undefined;
};

export const CurrentSubscription = ({
  setShowAmModal,
  subscription,
}: Props) => {
  const { t } = useTranslation();
  const { plan, upcomingInvoice, cancelAt, nextPeriodStart } =
    subscription || {};
  const { userData } = useAuth();
  const [showContactUsModal, setShowContactUsModal] = useState(false);
  const [isLegacyAccount, setIsLegacyAccount] = React.useState(true);
  const [currentPackageId, setCurrentPackageId] = React.useState(
    PackageName.LEGACY
  );

  const [currentPackageAdditionalInfo, setCurrentPackageAdditionalInfo] =
    React.useState(packageAdditionalInfo[PackageName.LEGACY]);

  const isBeingCanceled = cancelAt !== null;

  useEffect(() => {
    if (userData?.user?.packageDetails?.id) {
      setCurrentPackageId(userData?.user?.packageDetails?.id);
      setIsLegacyAccount(false);
      setCurrentPackageAdditionalInfo(
        packageAdditionalInfo[userData?.user?.packageDetails?.id]
      );
    }
  }, [userData]);

  return (
    <>
      {userData.trialAccount ? (
        <SubscriptionWrapper>
          <div className='div--left'>
            <SubscriptionDiv>
              <div className='plan--trial font--14'>
                {t('Trial active until {{date}}', {
                  date: dayjs(userData.signupDate)
                    .add(7, 'day')
                    .format(STANDARD_DATE_FORMAT),
                })}
              </div>
              <div className='plan--name'>{t('Covideo Trial')}</div>
            </SubscriptionDiv>
          </div>
          <div className='div--right'>
            <SubscriptionDiv>
              <div className='manage--btn'>
                <Button
                  onClick={() => setShowContactUsModal(true)}
                  text={t('Upgrade Plan')}
                  variant='primary'
                ></Button>
              </div>
            </SubscriptionDiv>
          </div>
        </SubscriptionWrapper>
      ) : isLegacyAccount ? (
        <SubscriptionWrapper>
          <div className='div--left'>
            <SubscriptionDiv>
              {isNull(cancelAt) ? (
                <div>{t('Active plan')}</div>
              ) : (
                <div className='plan--trial font--14'>
                  {t('Active until {{date}}', {
                    date:
                      cancelAt &&
                      dayjs(new Date(cancelAt * 1000)).format(
                        STANDARD_DATE_FORMAT
                      ),
                  })}
                </div>
              )}
              <div className='plan--name'>
                {(plan && plan.interval && plan.valid
                  ? plan.interval == 'year'
                    ? t('Covideo Annual')
                    : t('Covideo Monthly')
                  : (plan && !plan.valid) ||
                      (userData.customer && !userData.customer.stripeId)
                    ? t('Covideo Legacy Plan')
                    : null) || '--'}
              </div>
            </SubscriptionDiv>
            {!isNull(nextPeriodStart) ? (
              <SubscriptionDiv>
                <div className='font--14'>
                  {t('From {{date}}', {
                    date: !isNull(nextPeriodStart)
                      ? nextPeriodStart &&
                        dayjs(new Date(nextPeriodStart * 1000)).format(
                          STANDARD_DATE_FORMAT
                        )
                      : '--',
                  })}
                </div>
                <div className='plan--name--upcoming'>
                  {t('Covideo {{interval}}', {
                    interval:
                      plan && plan.interval == 'year'
                        ? t('Monthly')
                        : t('Annual'),
                  })}
                </div>
              </SubscriptionDiv>
            ) : null}
          </div>
          <div className='div--right'>
            {/* cancelAt is null that means subscription is activated */}
            {isNull(cancelAt) ? (
              <>
                {upcomingInvoice &&
                Object.keys(upcomingInvoice).length &&
                plan &&
                plan.valid ? (
                  <SubscriptionDivUntill>
                    {t('Next payment on')}&nbsp;
                    {upcomingInvoice.created ? (
                      <b>
                        {dayjs(new Date(upcomingInvoice.created * 1000)).format(
                          STANDARD_DATE_FORMAT
                        )}
                      </b>
                    ) : (
                      '--'
                    )}
                    :&nbsp;
                    {upcomingInvoice.amountDue
                      ? preparePrice(upcomingInvoice.amountDue, 'USD', true)
                      : '--'}{' '}
                  </SubscriptionDivUntill>
                ) : (plan && !plan.valid) ||
                  (userData.customer && !userData.customer.stripeId) ? (
                  <SubscriptionDivUntill>
                    {t('Contact your AM to discuss your current pricing plan')}
                  </SubscriptionDivUntill>
                ) : null}
              </>
            ) : null}
            <SubscriptionDiv>
              <div className='manage--btn'>
                <Button
                  onClick={() => setShowAmModal(true)}
                  text={t('Manage Subscription')}
                  variant='primary'
                ></Button>
              </div>
            </SubscriptionDiv>
          </div>
        </SubscriptionWrapper>
      ) : !isLegacyAccount ? (
        <Wrapper>
          <Row>
            <Plan>
              <ActivePlan isBeingCanceled={isBeingCanceled}>
                {!isBeingCanceled
                  ? t('Active plan')
                  : t('Active plan until {{date}}', {
                      date:
                        cancelAt &&
                        dayjs(new Date(cancelAt * 1000)).format(
                          STANDARD_DATE_FORMAT
                        ),
                    })}
              </ActivePlan>
              <Title>
                {t('Covideo {{title}}', {
                  title: currentPackageAdditionalInfo.title,
                })}
              </Title>
              {currentPackageId !== PackageName.FREE && (
                <BillingCycle>
                  <Price>
                    {upcomingInvoice?.amountDue
                      ? preparePrice(upcomingInvoice.amountDue, 'USD', true)
                      : '--'}
                  </Price>{' '}
                  {plan?.interval === 'year' ? t('per year') : t('per month')}
                </BillingCycle>
              )}
            </Plan>
            {currentPackageId !== PackageName.FREE &&
              !isEmpty(upcomingInvoice) && (
                <NextPayment>
                  {t('Next payment on')}{' '}
                  <b>
                    {upcomingInvoice &&
                      dayjs(new Date(upcomingInvoice?.created * 1000)).format(
                        STANDARD_DATE_FORMAT
                      )}
                  </b>
                </NextPayment>
              )}
            <ManagePlan>
              {currentPackageId === PackageName.FREE && (
                <Button
                  onClick={() => setShowContactUsModal(true)}
                  text={t('Upgrade to Pro')}
                  icon={<IoMdLock />}
                />
              )}
              {currentPackageId === PackageName.ENTERPRISE && (
                <Button
                  onClick={() => setShowContactUsModal(true)}
                  text={t('Manage Subscription')}
                  icon={<MdEdit />}
                  variant='secondary'
                />
              )}
              {[PackageName.PRO, PackageName.TEAMS].includes(
                currentPackageId
              ) && (
                <Button
                  onClick={() => setShowContactUsModal(true)}
                  text={t('Manage Subscription')}
                  icon={<MdEdit />}
                  variant='secondary'
                />
              )}
            </ManagePlan>
          </Row>
        </Wrapper>
      ) : null}
      {showContactUsModal && (
        <ModalContactAM
          handleModalClose={() => setShowContactUsModal(false)}
          subject={t('Covideo Subscription Update Request')}
          title={t('Manage Subscription')}
        />
      )}
    </>
  );
};
